// Kiosk list cell renderers — JSX-bearing helpers consumed by the
// kiosk-list config. Split into a `.tsx` sibling so the config itself
// (`kiosk-list.ts`) stays string-emitting like every other v2gen
// screen config; future generator runs can keep emitting `.ts`
// configs without learning JSX.
//
// CHG-2061 / ITER-4159 (kiosk-γ.1) — dashboard-v2 port of v1 kiosk
// list cell treatments at web/dashboard/src/pages/Kiosks.tsx:22-53,
// 148-158. Reader-type badge mirrors CHG-1198 / ITER-3317.

import type { ReactNode } from "react";
import type { Kiosk, KioskReaderType } from "../../api/types";

// CHG-1198 / ITER-3317 — Tap-to-Pay (Soft) vs M2 (Hardware) distinguishes
// tablet-on-Stripe-Terminal vs phone-with-NFC kiosks. Mirrors the v1
// badge at web/dashboard/src/pages/Kiosks.tsx:148-158. Renders via
// CSS-var tokens (composition rule 5) — no hardcoded color hex.
const READER_TYPE_BADGE: Record<KioskReaderType, { label: string; bg: string; fg: string }> = {
  tap_to_pay: {
    label: "Tap-to-Pay (Soft)",
    bg: "var(--color-primary-tint, rgba(99, 102, 241, 0.12))",
    fg: "var(--color-primary)",
  },
  m2: {
    label: "M2 (Hardware)",
    bg: "var(--color-bg-subtle)",
    fg: "var(--color-text-secondary)",
  },
};

export function readerTypeBadge(row: Kiosk): ReactNode {
  const tone = READER_TYPE_BADGE[row.reader_type] ?? READER_TYPE_BADGE.m2;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        height: 20,
        padding: "0 8px",
        fontSize: 11,
        fontWeight: 500,
        background: tone.bg,
        color: tone.fg,
        borderRadius: 4,
      }}
    >
      {tone.label}
    </span>
  );
}

// Online status — same 2-min / 10-min thresholds as v1
// (web/dashboard/src/pages/Kiosks.tsx:22-40). Returned as a keyed
// state so the renderer can style each case without re-evaluating.
type OnlineState = "online" | "idle" | "offline" | "never";

function onlineState(lastSeen?: string | null): OnlineState {
  if (!lastSeen) return "never";
  const diff = Date.now() - new Date(lastSeen).getTime();
  if (diff < 2 * 60 * 1000) return "online";
  if (diff < 10 * 60 * 1000) return "idle";
  return "offline";
}

const ONLINE_TONE: Record<OnlineState, { bg: string; label: string }> = {
  online:  { bg: "var(--color-success)",      label: "Online" },
  idle:    { bg: "var(--color-warning)",      label: "Idle" },
  offline: { bg: "var(--color-error)",        label: "Offline" },
  never:   { bg: "var(--color-text-subtle)",  label: "Never connected" },
};

export function onlineDot(row: Kiosk): ReactNode {
  const state = onlineState(row.last_seen_at);
  const tone = ONLINE_TONE[state];
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <span
        aria-hidden="true"
        style={{
          display: "inline-block",
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: tone.bg,
        }}
      />
      <span style={{ fontSize: 11, color: "var(--color-text-muted)" }}>
        {tone.label}
      </span>
    </span>
  );
}

// Battery from the embedded latest_heartbeat rollup. Em-dash when no
// heartbeat received. Three color buckets mirror v1's <15 / <30 / else.
export function batteryCell(row: Kiosk): ReactNode {
  const pct = row.latest_heartbeat?.battery_percent;
  if (pct == null) {
    return <span style={{ color: "var(--color-text-subtle)" }}>—</span>;
  }
  const color =
    pct < 15
      ? "var(--color-error)"
      : pct < 30
      ? "var(--color-warning)"
      : "var(--color-success)";
  return (
    <span style={{ fontSize: 11, fontWeight: 500, color }}>{pct}%</span>
  );
}

// App version off the heartbeat rollup; em-dash fallback.
export function appVersionCell(row: Kiosk): ReactNode {
  const v = row.latest_heartbeat?.app_version;
  if (!v) return <span style={{ color: "var(--color-text-subtle)" }}>—</span>;
  return (
    <span
      style={{
        fontSize: 11,
        fontFamily: "ui-monospace, SFMono-Regular, monospace",
        color: "var(--color-text-muted)",
      }}
    >
      {v}
    </span>
  );
}
